import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import moment from 'moment/moment';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import styles from '@components-clean/core/drag-drop-lists-core/drag-drop-item/style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { NamedItemType } from '@interfaces-clean/organisationLov.interface';
import { SubjectObservationType } from '@interfaces-clean/subject-monitoring/subject-observations';
import { SubjectReviewLog } from '@interfaces-clean/subject-monitoring/subject-review-log';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import OrganisationLovApi from '@apis/bff-api/central-monitoring/organisationLovs';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';
import SubjectReviewLogsApi from '@apis/bff-api/subject-monitoring/subject-review-log';

interface Props {
    reviewLog: SubjectReviewLog;
    studyRef: string;
    onSubmit: (id: number) => void;
    onClose: () => void;
}

export interface EditReviewLogForm {
    dataset: Date | null;
    association: string | null;
    panel: string | null;
    decision: { id: number; name: string; groupedBy: string; disabled: boolean } | null;
    comments: string | null;
    created_date: Date | null;
    created_by: string;
    updated_date: Date | null;
    updated_by: string | null;
    reviewLogId: number;
}

const COMPONENT_NAME = 'edit-subject-review-log-dialog';

const EditSubjectReviewLogDialog = ({ reviewLog, onClose, onSubmit, studyRef }: Props) => {
    const { t } = useTranslation('sm');

    const { data: observation, isLoading: loadingObservation } = SubjectObservationsApi.useGetDetails(
        studyRef,
        reviewLog.observation_id,
    );
    const { trigger: editReviewLog } = SubjectReviewLogsApi.useUpdate(studyRef);

    const { data: lovData, isLoading: loadingData } = OrganisationLovApi.useGet(
        studyRef,
        NamedItemType.SMReviewLogDecision,
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<EditReviewLogForm>({
        mode: 'all',
        defaultValues: {
            dataset: reviewLog.dataset ? new Date(reviewLog.dataset) : null,
            association: reviewLog.association ?? '',
            panel: reviewLog.panel ?? '',
            decision: reviewLog.decision ? { name: reviewLog.decision, id: reviewLog.decision_id } : null,
            comments: reviewLog.comments ?? '',
            created_date: reviewLog.created_date ? new Date(reviewLog.created_date) : null,
            created_by: reviewLog.created_by ?? '',
            updated_date: reviewLog.updated_date ? new Date(reviewLog.updated_date) : null,
            updated_by: reviewLog.updated_by ?? '',
        },
    });

    const decisionData = useMemo(() => {
        if (!lovData) return [];

        return lovData
            .filter((y) => y && y.name)
            .sort((a, b) => {
                if (a?.active !== b?.active) {
                    return a?.active ? -1 : 1;
                }

                if (a.name && b.name) {
                    return a.name?.localeCompare(b.name);
                }

                return 0;
            })
            .map((data) => ({
                name: data?.name ?? '',
                id: data?.id ?? 0,
                groupedBy: data?.active ? 'Active' : 'Inactive',
                disabled: reviewLog.decision
                    ? reviewLog.decision.toLowerCase() === data.name?.toLowerCase()
                        ? false
                        : !data.active
                    : !data.active,
                active: data?.active,
            }));
    }, [lovData, reviewLog.decision]);

    useEffect(() => {
        if (reviewLog.decision && reviewLog.decision_id && decisionData.length > 0) {
            const currentData = decisionData.find(
                (decisionDataList) => decisionDataList?.id === Number(reviewLog.decision_id),
            );

            if (currentData) {
                reset((currentDefaults) => {
                    return { ...currentDefaults, decision: currentData };
                });
            }
        }
    }, [lovData, reviewLog.decision_id]);

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const showDataListing = !reviewLog.panel && !!reviewLog.data_listing;
    const panelOrDataListingLabel = showDataListing
        ? t('monitoringToolkit.editObservationDialog.dataListing')
        : t('reviewLogDialog.panel');
    const panelOrDataListingValue = showDataListing ? reviewLog.data_listing : (reviewLog.panel ?? 'N/A');
    const panelOrDataListingInactive = showDataListing
        ? reviewLog.data_listing_active === 'No'
        : reviewLog.panel_active === 'No';

    const dialogTitle = `${t('common:edit')} ${t('reviewLogDialog.subjectTitle')}: ${reviewLog.record_id}`;
    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(reviewLog?.created_date).format('DD-MMM-YYYY')} by ${reviewLog?.created_by}`;
    const updatedBy = `${moment(reviewLog?.updated_date).format('DD-MMM-YYYY')} by ${reviewLog?.updated_by}`;

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const onEditReviewLog = async (data: EditReviewLogForm) => {
        try {
            const response = await editReviewLog({
                reviewLogDataId: Number(reviewLog.id),
                comments: data.comments,
                decision: data.decision?.name,
                decisionId: data.decision?.id,
                observationId: Number(reviewLog.observation_id),
            });

            if (response?.success) {
                return response.id!;
            }
            return 0;
        } catch {
            return 0;
        }
    };

    const submitAndClose = async (formData: EditReviewLogForm) => {
        const id = await onEditReviewLog(formData);
        onSubmit(id);
    };

    const handlePrimarySubmit = () => handleSubmit((data) => submitAndClose(data))();

    return (
        <DraggableDialog
            dataTestId="edit-subject-review-log-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={handlePrimarySubmit}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} width={500} />}
            createdUpdatedBackgroundColour="gray"
            initialHeight={632}
            initialWidth={531}
            additionalInfo={
                <ObservationDetails
                    observation={observation}
                    observationLoading={loadingObservation}
                    type={SubjectObservationType.Manual}
                    area="SM"
                />
            }
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="100%"
        >
            <div className={cn(style.editSiteObservationDialog, style.rightBorderStyle, 'k-w-full', 'k-h-full')}>
                <div className={cn(style.editSiteObservationInfo, 'k-pt-sm k-pb-sm k-pl-md k-pr-md')}>
                    <div className={cn(style.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-8/12 k-gap-2">
                            <div className="k-font-medium">{t('reviewLogDialog.type')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel, 'k-pl-1')}>{reviewLog.type}</div>
                        </div>
                        <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-6/12 k-gap-2">
                            <div className="k-font-medium">{t('reviewLogDialog.subject')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel)}>{reviewLog.subject}</div>
                        </div>
                        <div className="k-flex k-d-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-7/12 k-gap-2">
                            <div className="k-font-medium">{t('reviewLogDialog.dataset')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel)}>{reviewLog.dataset}</div>
                        </div>
                    </div>
                </div>
                <div className={cn(style.editSiteObservationInfo, 'k-pt-sm k-pl-md k-pr-md')}>
                    <div className="k-d-flex k-justify-content-left k-align-items-center k-pb-sm">
                        <div className="k-pr-2 k-align-items-center k-font-medium">
                            {t('reviewLogDialog.association')}
                        </div>
                        <div className={cn(style.editStudyReviewLogLabel, 'k-d-flex k-align-items-center')}>
                            {reviewLog.association}
                            {reviewLog?.association_active === 'No' && (
                                <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                                    <InactiveIcon
                                        toolTipText={t('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                        className="k-mr-2"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={cn(style.editSiteObservationInfo, style.dividerBottom, 'k-pt-sm k-pl-md k-pr-md')}>
                    <div className="k-d-flex k-justify-content-left k-align-items-center k-pb-sm">
                        <div className="k-pr-2 k-align-items-center k-font-medium">{panelOrDataListingLabel}</div>
                        <div className={cn(style.editStudyReviewLogLabel, 'k-d-flex k-align-items-center')}>
                            {panelOrDataListingValue}
                            {panelOrDataListingInactive && (
                                <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                                    <InactiveIcon
                                        toolTipText={t('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                        className="k-mr-2"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <form id="edit-subject-review-log" className="k-w-full k-pt-sm">
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md k-pl-md k-pr-md">
                        <BasicDropDownController
                            label={t('reviewLogDialog.decision')}
                            control={control}
                            name="decision"
                            data-testid="edit-review-log-decision"
                            data={decisionData}
                            textField="name"
                            groupField="groupedBy"
                            dataItemKey="id"
                            filterable
                            loading={loadingData}
                            allowClear
                            activeField="active"
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-lg k-pl-md k-pr-md">
                        <TextAreaController
                            maxLength={3000}
                            control={control}
                            name="comments"
                            data-testid="edit-review-log-comments"
                            label={t('reviewLogDialog.comments')}
                            style={{
                                height: '299px',
                                overflow: 'auto',
                            }}
                            readOnly={isSubmitting}
                        />
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default EditSubjectReviewLogDialog;
